<template>
  <div class="app-version-manage">
    <!-- 顶部导航 -->
    <header class="page-header">
      <span class="header-back" @click="$router.back()">←</span>
      <span class="header-title">APP 版本管理</span>
      <span class="header-action" @click="showCreateDialog">＋</span>
    </header>

    <!-- 平台切换 -->
    <div class="platform-tabs">
      <div
        :class="['tab-item', { active: currentPlatform === 'ANDROID' }]"
        @click="switchPlatform('ANDROID')"
      >Android</div>
      <div
        :class="['tab-item', { active: currentPlatform === 'IOS' }]"
        @click="switchPlatform('IOS')"
      >iOS</div>
    </div>

    <!-- 版本列表 -->
    <div class="version-list" v-loading="loading">
      <div v-if="versions.length === 0 && !loading" class="empty-state">
        <div class="empty-icon">📱</div>
        <div class="empty-text">暂无版本记录</div>
        <el-button type="primary" size="small" @click="showCreateDialog">发布第一个版本</el-button>
      </div>

      <div
        v-for="item in versions"
        :key="item.id"
        class="version-card"
      >
        <div class="card-header">
          <div class="version-info">
            <span class="version-name">v{{ item.versionName }}</span>
            <span class="version-code">({{ item.versionCode }})</span>
          </div>
          <div class="card-tags">
            <span v-if="item.forceUpdate" class="tag tag-danger">强制更新</span>
            <span :class="['tag', item.publishedAt ? 'tag-success' : 'tag-warning']">
              {{ item.publishedAt ? '已发布' : '未发布' }}
            </span>
          </div>
        </div>

        <div class="card-body" v-if="item.updateDescription">
          <div class="desc-label">更新说明</div>
          <div class="desc-text">{{ item.updateDescription }}</div>
        </div>

        <div class="card-body" v-if="item.downloadUrl">
          <div class="desc-label">下载链接</div>
          <div class="desc-text link-text">{{ item.downloadUrl }}</div>
        </div>

        <div class="card-footer" v-if="item.publishedAt">
          <span class="publish-time">发布于 {{ item.publishedAt }}</span>
        </div>

        <div class="card-actions">
          <button
            class="action-btn action-edit"
            @click="editVersion(item)"
          >编辑</button>
          <button
            v-if="!item.publishedAt"
            class="action-btn action-primary"
            @click="publishVersion(item)"
          >发布</button>
          <button
            class="action-btn action-danger"
            @click="deleteVersion(item)"
          >删除</button>
        </div>
      </div>
    </div>

    <!-- 创建/编辑版本弹窗 -->
    <el-dialog :title="(editingId ? '编辑版本' : '发布新版本') + ' - ' + (currentPlatform === 'ANDROID' ? 'Android' : 'iOS')" :visible.sync="createDialogVisible" width="90%" :append-to-body="true">
      <el-form :model="form" :rules="rules" ref="versionForm" label-width="80px" label-position="top">
        <el-form-item label="版本号" prop="versionCode">
          <el-input-number v-model="form.versionCode" :min="1" style="width: 100%"></el-input-number>
        </el-form-item>
        <el-form-item label="版本名" prop="versionName">
          <el-input v-model="form.versionName" placeholder="如 1.0.1"></el-input>
        </el-form-item>
        <el-form-item label="下载链接" prop="downloadUrl">
          <el-input v-model="form.downloadUrl" placeholder="App Store 或 APK 下载地址"></el-input>
        </el-form-item>
        <el-form-item label="强制更新">
          <el-switch v-model="form.forceUpdate"></el-switch>
          <span style="margin-left: 10px; color: #999; font-size: 12px;">开启后用户无法跳过</span>
        </el-form-item>
        <el-form-item label="更新说明">
          <el-input v-model="form.updateDescription" type="textarea" :rows="4" placeholder="更新内容说明"></el-input>
        </el-form-item>
        <el-form-item label="立即发布">
          <el-switch v-model="form.publishNow"></el-switch>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="createDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm" :loading="submitting">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import api from '@/services/api'

export default {
  name: 'AppVersionManage',
  data() {
    return {
      currentPlatform: 'ANDROID',
      versions: [],
      loading: false,
      createDialogVisible: false,
      editingId: null,
      submitting: false,
      form: {
        versionCode: 1,
        versionName: '',
        downloadUrl: '',
        forceUpdate: false,
        updateDescription: '',
        publishNow: true
      },
      rules: {
        versionCode: [{ required: true, message: '请输入版本号', trigger: 'blur' }],
        versionName: [{ required: true, message: '请输入版本名', trigger: 'blur' }],
        downloadUrl: [{ required: true, message: '请输入下载链接', trigger: 'blur' }]
      }
    }
  },
  mounted() {
    this.loadVersions()
  },
  methods: {
    switchPlatform(platform) {
      this.currentPlatform = platform
      this.loadVersions()
    },
    async loadVersions() {
      this.loading = true
      try {
        const res = await api.get('/app-version/list', { params: { platform: this.currentPlatform } })
        // 响应拦截器已经处理过数据格式，res 直接就是数组
        this.versions = Array.isArray(res) ? res : (res?.data || [])
      } catch (e) {
        this.$toast.error('加载版本列表失败')
        console.error('加载版本列表失败:', e)
      } finally {
        this.loading = false
      }
    },
    showCreateDialog() {
      this.editingId = null
      this.form = {
        versionCode: (this.versions.length > 0 ? Math.max(...this.versions.map(v => v.versionCode)) + 1 : 1),
        versionName: '',
        downloadUrl: '',
        forceUpdate: false,
        updateDescription: '',
        publishNow: true
      }
      this.createDialogVisible = true
      this.$nextTick(() => {
        this.$refs.versionForm?.clearValidate()
      })
    },
    editVersion(item) {
      this.editingId = item.id
      this.form = {
        versionCode: item.versionCode,
        versionName: item.versionName,
        downloadUrl: item.downloadUrl,
        forceUpdate: item.forceUpdate || false,
        updateDescription: item.updateDescription || '',
        publishNow: !!item.publishedAt
      }
      this.createDialogVisible = true
      this.$nextTick(() => {
        this.$refs.versionForm?.clearValidate()
      })
    },
    submitForm() {
      this.$refs.versionForm.validate(async (valid) => {
        if (!valid) return
        this.submitting = true
        try {
          if (this.editingId) {
            await api.put(`/app-version/${this.editingId}`, {
              ...this.form,
              platform: this.currentPlatform
            })
            this.$toast.success('版本更新成功')
          } else {
            await api.post('/app-version/create', {
              ...this.form,
              platform: this.currentPlatform
            })
            this.$toast.success('版本创建成功')
          }
          this.createDialogVisible = false
          this.loadVersions()
        } catch (e) {
          this.$toast.error((this.editingId ? '更新' : '创建') + '失败: ' + (e.response?.data?.message || e.message))
        } finally {
          this.submitting = false
        }
      })
    },
    async publishVersion(row) {
      try {
        await api.post(`/app-version/${row.id}/publish`)
        this.$toast.success('发布成功')
        this.loadVersions()
      } catch (e) {
        this.$toast.error('发布失败')
      }
    },
    async deleteVersion(row) {
      try {
        await this.$confirm(`确定删除版本 ${row.versionName}？`, '确认', { type: 'warning' })
        await api.delete(`/app-version/${row.id}`)
        this.$toast.success('删除成功')
        this.loadVersions()
      } catch (e) {
        if (e !== 'cancel') this.$toast.error('删除失败')
      }
    }
  }
}
</script>

<style scoped>
.app-version-manage {
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 20px;
}

.page-header {
  position: sticky;
  top: 0;
  z-index: 100;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  padding: calc(12px + env(safe-area-inset-top, 0px)) 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-back,
.header-action {
  font-size: 20px;
  font-weight: bold;
  cursor: pointer;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-title {
  font-size: 17px;
  font-weight: 600;
}

.platform-tabs {
  display: flex;
  background: #fff;
  margin: 12px;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
}

.tab-item {
  flex: 1;
  text-align: center;
  padding: 12px 0;
  font-size: 14px;
  font-weight: 500;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
}

.tab-item.active {
  color: #667eea;
  font-weight: 600;
}

.tab-item.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 40px;
  height: 3px;
  background: #667eea;
  border-radius: 2px;
}

.version-list {
  padding: 0 12px;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 12px;
}

.empty-text {
  font-size: 14px;
  color: #999;
  margin-bottom: 20px;
}

.version-card {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.version-info {
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.version-name {
  font-size: 18px;
  font-weight: 700;
  color: #333;
}

.version-code {
  font-size: 13px;
  color: #999;
}

.card-tags {
  display: flex;
  gap: 6px;
}

.tag {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 10px;
  font-weight: 500;
}

.tag-success {
  background: #e8f5e9;
  color: #4caf50;
}

.tag-warning {
  background: #fff3e0;
  color: #ff9800;
}

.tag-danger {
  background: #fce4ec;
  color: #e53935;
}

.card-body {
  margin-bottom: 10px;
}

.desc-label {
  font-size: 12px;
  color: #999;
  margin-bottom: 4px;
}

.desc-text {
  font-size: 13px;
  color: #333;
  line-height: 1.5;
  word-break: break-all;
}

.link-text {
  color: #667eea;
  font-size: 12px;
}

.card-footer {
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
  margin-bottom: 10px;
}

.publish-time {
  font-size: 12px;
  color: #999;
}

.card-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

.action-btn {
  border: none;
  padding: 6px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.2s;
}

.action-btn:active {
  opacity: 0.7;
}

.action-primary {
  background: #667eea;
  color: #fff;
}

.action-edit {
  background: #e3f2fd;
  color: #1976d2;
}

.action-danger {
  background: #fce4ec;
  color: #e53935;
}
</style>
